/**
 * @license
 * Copyright UX Stack. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at LICENSE.md
 */

import * as uxstackpb from '@uxstack/sdk-proto-js/uxstack/sdk/v1/sdk_pb';

export class EventContextAgent extends uxstackpb.EventContextAgent {
  constructor() {
    super({
      screenSize: new uxstackpb.Size({
        width: window.screen.width,
        height: window.screen.height,
      }),
      userAgent: navigator.userAgent,
      language: navigator.language,
      pixelRatio: window.devicePixelRatio,
      vendor: navigator.vendor,
      encoding: document.characterSet,
    });

    const vpSize = this._getViewportSize();

    this.viewportSize = new uxstackpb.Size({
      width: vpSize.width,
      height: vpSize.height,
    });

    this.orientation = this._getOrientation();
    this.platform = this._getPlatform();
    this.isMobile = this._isMobile();
  }

  private _isMobile(): boolean {
    if (navigator.userAgentData && navigator.userAgentData.mobile) {
      return true;
    }

    return /Mobi/i.test(navigator.userAgent);
  }

  private _getPlatform(): string {
    if (navigator.userAgentData && navigator.userAgentData.platform) {
      return navigator.userAgentData.platform;
    }

    // see: https://stackoverflow.com/a/19883965/9722369
    switch (navigator.platform) {
      case 'Win32':
      case 'Win64':
        return 'Windows';
      case 'MacIntel':
      case 'MacPPC':
      case 'Mac68K':
        return 'macOS';
      case 'iPhone':
      case 'iPod':
        return 'iOS';
      case 'iPad':
        return 'iPadOS';
      case 'Android':
        return 'Android';
      case 'Linux':
        return 'Linux';
      default:
        return navigator.platform;
    }
  }

  // see: https://stackoverflow.com/a/44709318/9722369
  private _getOrientation(): string {
    if (screen.orientation && screen.orientation.type) {
      return screen.orientation.type as string;
    }

    const mql = window.matchMedia('(orientation: portrait)');

    if (mql.matches) {
      return 'portrait-primary';
    } else {
      return 'landscape-primary';
    }
  }

  private _getViewportSize(): { height: number; width: number } {
    // This works for all browsers except IE8 and before
    if (window.innerWidth != null) {
      return {
        height: window.innerHeight,
        width: window.innerWidth,
      };
    }

    // For IE (or any browser) in Standards mode
    const d = window.document;

    if (d.compatMode === 'CSS1Compat') {
      return {
        height: d.documentElement.clientHeight,
        width: d.documentElement.clientWidth,
      };
    }

    // For browsers in Quirks mode
    return {
      height: d.body.clientHeight,
      width: d.body.clientWidth,
    };
  }
}
